import { useEffect } from 'react';

/**
 * useTauriWindowFloor — keeps a narrow desktop window from becoming the phone
 * layout.
 *
 * The responsive switch to touch chrome (bottom nav, mobile header,
 * off-canvas sidebar drawer) is a width media query — `max-width: 767px` in
 * Sidebar.css / MobileHeader.css / MobileBottomNav.css. That is exactly right
 * for an actual phone viewport, which is all the query can see. It cannot
 * tell a phone apart from a desktop window someone snapped to a slice of the
 * screen, and `src-tauri/tauri.conf.json` lets the window shrink to 350px —
 * well past the 768px line — so nothing currently stops a resize from
 * silently turning the desktop app into the phone app.
 *
 * A narrow desktop window should settle into a narrower *desktop* (the
 * existing 768–1024px tablet range already does this: rail sidebar, desktop
 * header, no bottom nav) rather than cross into touch chrome meant for a
 * phone. So this raises the window's minimum width to the same breakpoint at
 * runtime, once the webview is up — instead of widening every width check in
 * the layout (Sidebar, Header, MobileBottomNav, and the AppShell
 * `matchMedia` all key off raw width independently; re-pointing all of them
 * at "am I actually a phone?" is a much bigger, riskier change than fixing
 * where the window itself is allowed to go). Height keeps the config's
 * existing floor (600) — only width changes here.
 *
 * No-op outside Tauri (`window.__TAURI_INTERNALS__` unset): a real mobile
 * browser is supposed to reach the phone layout, and this must never affect
 * the web build.
 */
export const DESKTOP_WIDTH_FLOOR = 768; // mirrors the max-width:767px phone-chrome switch
export const DESKTOP_HEIGHT_FLOOR = 600; // mirrors tauri.conf.json's existing minHeight

export default function useTauriWindowFloor() {
    useEffect(() => {
        if (!window.__TAURI_INTERNALS__) return;
        let cancelled = false;

        (async () => {
            try {
                const { getCurrentWindow, LogicalSize } = await import('@tauri-apps/api/window');
                if (cancelled) return;
                await getCurrentWindow().setMinSize(new LogicalSize(DESKTOP_WIDTH_FLOOR, DESKTOP_HEIGHT_FLOOR));
            } catch (err) {
                // Best-effort: if the platform call fails, the window just keeps
                // its config-time floor. Not user-facing — there is nothing a
                // user could do about it.
                console.debug('[useTauriWindowFloor] Could not raise the window floor:', err?.message || err);
            }
        })();

        return () => { cancelled = true; };
    }, []);
}
